import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";

import { useCurrency } from "@/lib/currency";
import { chargeDisclosure } from "@/lib/booking-display";
import { getBookingByCodePublic } from "@/lib/booking.functions";
import { Skeleton } from "@/components/ui/skeleton";
import logoFullAsset from "@/assets/mostrando-mexico-logo-2026.png.asset.json";

function formatDate(iso: string, lang: string) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return new Intl.DateTimeFormat(lang, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(d);
}

type Booking = {
  booking_code?: string | null;
  tour_title?: string | null;
  customer_name?: string | null;
  travel_date?: string | null;
  start_time?: string | null;
  travelers?: number | null;
  currency?: string | null;
  total?: number | null;
  status?: string | null;
  source?: string | null;
  bokun_confirmation_code?: string | null;
  meeting_point?: string | null;
  extras?: { title: string; quantity: number }[];
};

type BookingResult =
  | { ok: true; booking: Booking }
  | { ok: false; booking: null };

const isDemoCode = (code: string) => code.toUpperCase().startsWith("DEMO");

const bookingQuery = (code: string) =>
  queryOptions({
    queryKey: ["booking", code],
    queryFn: async (): Promise<BookingResult> => {
      // DEMO codes never hit the database: they render the sales preview.
      if (isDemoCode(code)) return { ok: false, booking: null };
      const res = await getBookingByCodePublic({ data: { code } });
      if (!res.booking) return { ok: false, booking: null };
      return { ok: true, booking: res.booking as Booking };
    },
    staleTime: 30_000,
  });

export const Route = createFileRoute("/booking/$code")({
  head: ({ params }) => ({
    meta: [{ title: `Reserva ${params.code} — Mostrando México` }, { name: "robots", content: "noindex,nofollow" }, { name: "description", content: "Consulta tu confirmación de reserva en Mostrando México." }, { property: "og:title", content: "Tu reserva — Mostrando México" }, { property: "og:description", content: "Confirmación y detalles de tu experiencia." }, { property: "og:type", content: "website" }, { name: "twitter:card", content: "summary" }],
  }),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(bookingQuery(params.code)),
  component: BookingPage,
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-xl p-8 text-center">
      <h1 className="text-xl font-semibold">No se pudo cargar la reserva</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
});

function BookingPage() {
  const { code } = Route.useParams();
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="h-1 bg-gradient-to-r from-primary to-gold" />
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-3">
          <Link to="/" aria-label="Mostrando México — inicio" className="inline-flex items-center">
            <img src={logoFullAsset.url} alt="Mostrando México" width={1920} height={656} className="h-8 w-auto sm:h-9" />
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            {t("bookingFlow.voucher.back")}
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-12">
        <Suspense fallback={<Skeleton className="h-64 w-full" />}>
          <BookingBody code={code} />
        </Suspense>
      </main>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

function BookingBody({ code }: { code: string }) {
  const { t, i18n } = useTranslation();
  const { currency, format, rates } = useCurrency();
  const { data } = useSuspenseQuery(bookingQuery(code));
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
    `https://mostrandomexico.lovable.app/booking/${code}`,
  )}`;

  // ── Sales preview: only for codes that explicitly start with DEMO ────────
  if (isDemoCode(code)) {
    const barcodeUrl = `https://barcodeapi.org/api/code128/${encodeURIComponent(code)}`;
    return (
      <article className="space-y-6">
        <div className="rounded-lg border bg-card p-8 text-center">
          <div className="mb-2 text-sm uppercase tracking-wide text-muted-foreground">
            {t("bookingFlow.voucher.demoTitle")}
          </div>
          <h1 className="text-3xl font-bold">{code}</h1>
          <div className="mt-3 inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            {t("bookingFlow.voucher.statusConfirmed")}
          </div>
        </div>
        <div className="grid gap-4 rounded-lg border bg-card p-6 sm:grid-cols-2">
          <div className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">{t("bookingFlow.voucher.customer")}:</span> Carla M.</div>
            <div><span className="text-muted-foreground">{t("bookingFlow.voucher.activity")}:</span> Turtle Encounter &amp; Reef Snorkel — Akumal</div>
            <div><span className="text-muted-foreground">{t("bookingFlow.voucher.date")}:</span> 2026-06-07 · 09:00</div>
            <div><span className="text-muted-foreground">{t("bookingFlow.voucher.travelers")}:</span> 2</div>
            <div><span className="text-muted-foreground">{t("bookingFlow.voucher.total")}:</span> 178.00 USD</div>
          </div>
          <div className="flex flex-col items-center justify-center gap-3">
            <img loading="lazy" decoding="async" src={qrUrl} alt="QR" className="rounded-md border bg-white p-2" />
            <img loading="lazy" decoding="async" src={barcodeUrl} alt="Barcode" className="h-12 rounded-md border bg-white p-1" />
          </div>
        </div>
        <p className="text-center text-sm text-muted-foreground">{t("bookingFlow.voucher.demoNote")}</p>
      </article>
    );
  }

  // ── Unknown code: safe "not found" state, never invented data ────────────
  if (!data.ok || !data.booking) {
    return (
      <article className="space-y-4 rounded-lg border bg-card p-8 text-center">
        <h1 className="text-xl font-semibold">{t("bookingFlow.voucher.notFoundTitle")}</h1>
        <p className="text-sm text-muted-foreground">{t("bookingFlow.voucher.notFoundBody")}</p>
        <p className="font-mono text-sm">{code}</p>
        <Link to="/" className="inline-block text-sm underline">{t("bookingFlow.ret.home")}</Link>
      </article>
    );
  }

  const b = data.booking;
  const confirmed = b.status === "confirmed";
  const status = confirmed ? t("bookingFlow.voucher.statusConfirmed") : (b.status ?? "pending").toUpperCase();
  const dateLine = [b.travel_date ? formatDate(b.travel_date, i18n.language || "es") : null, b.start_time].filter(Boolean).join(" · ");

  return (
    <article className="space-y-6">
      <div className="rounded-2xl border bg-card p-6 text-center shadow-sm sm:p-8">
        <div
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold tracking-wide ${
            confirmed ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"
          }`}
        >
          {confirmed && <span aria-hidden>✓</span>}
          {status}
        </div>
        <h1 className="mt-3 text-2xl font-bold text-foreground sm:text-3xl">
          {confirmed ? t("bookingFlow.voucher.readyLine") : t("bookingFlow.voucher.createdTitle")}
        </h1>
        <p className="mt-1 font-mono text-sm tracking-wider text-muted-foreground">{b.booking_code ?? code}</p>
        {!confirmed && (
          <p className="mt-3 text-sm text-muted-foreground">{t("bookingFlow.voucher.pending")}</p>
        )}
        {confirmed && (
          <div className="mt-6 flex justify-center">
            <div className="rounded-xl border bg-white p-4">
              <img loading="lazy" decoding="async" src={qrUrl} alt="QR" width={220} height={220} className="block h-[220px] w-[220px]" />
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3 rounded-2xl border bg-card p-6">
        {b.tour_title && <Row label={t("bookingFlow.voucher.activity")} value={b.tour_title} />}
        {dateLine && <Row label={t("bookingFlow.voucher.date")} value={dateLine} />}
        {b.travelers != null && <Row label={t("bookingFlow.voucher.travelers")} value={String(b.travelers)} />}
        {b.total != null && (
          <Row
            label={t("bookingFlow.voucher.total")}
            value={`${format(Number(b.total), b.currency, { locale: i18n.language, decimals: 2 })} · ${chargeDisclosure(Number(b.total), b.currency ?? "MXN", currency, i18n.language, rates)}`}
          />
        )}
        <Row label={t("bookingFlow.voucher.status")} value={status} />
        {b.customer_name && <Row label={t("bookingFlow.voucher.customer")} value={b.customer_name} />}
        {b.meeting_point && <Row label={t("bookingFlow.voucher.meetingPoint")} value={b.meeting_point} />}
        {b.bokun_confirmation_code && (
          <Row label={t("bookingFlow.voucher.operatorCode")} value={b.bokun_confirmation_code} />
        )}
        {!!b.extras?.length && (
          <Row
            label={t("bookingFlow.voucher.extras")}
            value={b.extras.map((e) => `${e.title} ×${e.quantity}`).join(", ")}
          />
        )}
      </div>

      <p className="text-center text-sm text-muted-foreground">{t("bookingFlow.voucher.saveCode")}</p>
    </article>
  );
}
